import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { insertAccountCreationChangeLog } from './accountCreationChangeLog';
import { PAYMENT_TERMS_LABEL } from './accountCreationUi';
import type {
  AccountCreationFinanceData,
  AccountCreationFormData,
  ApplicationSubmission,
  PaymentTermsOption,
} from '../types/applicationForm';

export type NavSyncStatus = 'pending' | 'success' | 'failed';

export type NavCustomerCardPayload = Record<string, string | number | boolean>;

export type PushAccountCreationToNavResult = {
  ok: boolean;
  error?: string;
  customer?: Record<string, unknown> | null;
};

/** Portal payment_terms → NAV Payment_Terms_Code (adjust if NAV codes differ). */
const PAYMENT_TERMS_TO_NAV: Record<PaymentTermsOption, string> = {
  cod: 'COD',
  '30_days': '30 DAYS',
  '45_days': '45 DAYS',
  '60_days': '60 DAYS',
  paynow: 'PAYNOW',
  cod_1: 'COD-1',
  cod_3: 'COD-3',
};

function joinAddressParts(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(', ')
    .slice(0, 100);
}

function paymentTermsCode(value: string | null | undefined): string {
  if (!value) return '';
  if (value in PAYMENT_TERMS_TO_NAV) {
    return PAYMENT_TERMS_TO_NAV[value as PaymentTermsOption];
  }
  return PAYMENT_TERMS_LABEL[value as PaymentTermsOption] ?? value.trim();
}

/** Build CustomerCard JSON body from an approved Account Creation row. */
export function buildNavCustomerCardPayload(
  row: ApplicationSubmission,
): NavCustomerCardPayload {
  const form = row.form_data as AccountCreationFormData;
  const finance = (row.finance_data ?? {}) as AccountCreationFinanceData;
  const no = row.customer_code?.trim() || '';
  const name = row.company_name?.trim() || '';

  const address = joinAddressParts(form.billing_street, form.billing_building);
  const address2 = joinAddressParts(form.billing_unit);
  const salesperson =
    finance.salesperson_code?.trim() || form.salesperson_code?.trim() || '';

  // Only send fields that exist on CustomerCard and are usually safe lookups.
  // Do not auto-map Gen_Bus_Posting_Group — portal business group ≠ NAV posting group.
  const payload: NavCustomerCardPayload = {
    No: no,
    Name: name.slice(0, 50),
    Address: address,
    Address_2: address2,
    Post_Code: (form.billing_postal ?? '').trim().slice(0, 20),
    Phone_No: (form.finance_contact_no ?? '').trim().slice(0, 30),
    Contact: (form.finance_contact_name ?? '').trim().slice(0, 50),
    E_Mail: (form.finance_email ?? '').trim().slice(0, 80),
    Salesperson_Code: salesperson.slice(0, 20),
    Customer_Price_Group: (finance.customer_price_group ?? '').trim().slice(0, 20),
    Payment_Terms_Code: paymentTermsCode(finance.payment_terms).slice(0, 20),
    Territory_Code: (finance.territory_code ?? '').trim().slice(0, 20),
  };

  const outlet = (form.outlet_name ?? '').trim();
  if (outlet && outlet.toLowerCase() !== name.toLowerCase()) {
    payload.Name_2 = outlet.slice(0, 50);
  }

  return payload;
}

async function callNavOdataProxy(body: Record<string, unknown>): Promise<{
  ok: boolean;
  error?: string;
  customer?: Record<string, unknown> | null;
  exists?: boolean;
}> {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    return {
      ok: false,
      error: 'VITE_DEM_EMAIL_PROXY_KEY is not configured in this portal build.',
    };
  }

  const res = await fetch('/nav-odata-api', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-DEM-Email-Key': proxyKey,
    },
    body: JSON.stringify(body),
  });

  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    customer?: Record<string, unknown> | null;
    exists?: boolean;
  };

  if (!res.ok || payload.ok === false) {
    return {
      ok: false,
      error: payload.error || `NAV proxy failed (HTTP ${res.status})`,
      exists: payload.exists,
      customer: payload.customer ?? null,
    };
  }

  return {
    ok: true,
    customer: payload.customer ?? null,
    exists: payload.exists,
  };
}

export async function updateAccountCreationNavSync(
  submissionId: string,
  input: {
    status: NavSyncStatus;
    error?: string | null;
  },
): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db
    .from('account_creation_submissions')
    .update({
      nav_sync_status: input.status,
      nav_synced_at: new Date().toISOString(),
      nav_sync_error: input.error?.trim() || null,
    })
    .eq('id', submissionId)
    .eq('status', 'approved');
  if (error) throw new Error(formatSupabaseError(error));
}

/**
 * Push approved Account Creation to NAV CustomerCard (manual).
 * Keeps Portal status = approved; records nav_sync_* + change log.
 */
export async function pushAccountCreationToNav(
  row: ApplicationSubmission,
  actor?: string | null,
): Promise<PushAccountCreationToNavResult> {
  if (row.status !== 'approved') {
    return { ok: false, error: 'Only approved applications can be pushed to NAV.' };
  }
  const code = row.customer_code?.trim();
  if (!code) {
    return { ok: false, error: 'Customer code is required before pushing to NAV.' };
  }
  if (!row.company_name?.trim()) {
    return { ok: false, error: 'Company name is required before pushing to NAV.' };
  }

  const customer = buildNavCustomerCardPayload(row);
  const result = await callNavOdataProxy({
    action: 'createCustomer',
    customer,
  });

  if (!result.ok) {
    try {
      await updateAccountCreationNavSync(row.id, {
        status: 'failed',
        error: result.error ?? 'NAV push failed.',
      });
      await insertAccountCreationChangeLog({
        submissionId: row.id,
        changedBy: actor,
        changeType: 'nav_push',
        summary: `NAV push failed: ${result.error ?? 'unknown error'}`,
        beforeJson: { nav_sync_status: row.nav_sync_status ?? null },
        afterJson: { nav_sync_status: 'failed', customer },
      });
    } catch {
      /* non-blocking */
    }
    return { ok: false, error: result.error ?? 'NAV push failed.' };
  }

  try {
    await updateAccountCreationNavSync(row.id, { status: 'success', error: null });
    await insertAccountCreationChangeLog({
      submissionId: row.id,
      changedBy: actor,
      changeType: 'nav_push',
      summary: `Pushed to NAV Customer ${code}`,
      beforeJson: { nav_sync_status: row.nav_sync_status ?? null },
      afterJson: {
        nav_sync_status: 'success',
        customer_no: code,
        nav_customer: result.customer ?? null,
      },
    });
  } catch (err) {
    return {
      ok: false,
      error:
        err instanceof Error
          ? `Created in NAV but failed to save sync status: ${err.message}`
          : 'Created in NAV but failed to save sync status.',
      customer: result.customer,
    };
  }

  return { ok: true, customer: result.customer };
}
